import { Component, OnInit, OnDestroy, signal, Signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ShopCartService } from './service/shop-cart.service';
import { SyncPricesCartService } from '../../../core/service/sync-prices-cart/sync-prices-cart.service';
import { Router } from '@angular/router';
import { LoaderSubitoService } from '../../components/loader-subito/service/loader-subito.service';
import { LoaderSubitoComponent } from '../../components/loader-subito/loader-subito.component';
import { SelectedAddressService } from '../../../core/service/selected-address.service';
import { ModalEmptyAddressesService } from '../modal-empty-addresses-user/service/modal-empty-addresses.service';
import { ModalEmptyAddressesUserComponent } from '../modal-empty-addresses-user/modal-empty-addresses-user.component';
import { CartStorageService } from '../../../core/service/cartStorage/cart-storage.service';
import { Product } from '../../../core/service/cart-apis/interfaces/get-cart.interface';
import { CartModel } from '../../../store/cart/cart.model';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { PrePurchaseService } from '../../../core/service/pre-purchase/pre-purchase.service';
import { Subscription } from 'rxjs';
import { PrePurchaseResponseCart, ProductsAble } from '../../../core/service/pre-purchase/interfaces/pre-purchase.interface';
import { NotificationsService } from '../notifications/service/notifications.service';

@Component({
  selector: 'shared-shop-cart',
  standalone: true,
  imports: [CommonModule, LoaderSubitoComponent, ModalEmptyAddressesUserComponent],
  templateUrl: './shop-cart.component.html',
  styleUrls: ['./shop-cart.component.css']
})
export class ShopCartComponent implements OnInit, OnDestroy {
  // Signals reactivos para el estado del carrito
  cartProducts = signal<Product[]>([]);
  cartProductsDisabled = signal<Product[]>([]);
  cartTotal: Signal<number | undefined>;
  isClearing = signal<boolean>(false);
  removingProductId = signal<string | null>(null);
  updatingProductId = signal<string | null>(null);
  isUpdatingSubtotal = signal<boolean>(false);
  nameEstablishment: Signal<string | undefined>;
  hasDisabledProducts = signal<boolean>(false);

  // Ruta de la imagen por defecto
  readonly defaultImage = 'assets/imagen_default.png';

  // Mapa de signals para las cantidades de productos
  private productQuantities = new Map<string, any>();

  // Mapa para controlar el estado expandido de cada producto
  private expandedProducts = new Map<string, any>();

  // Suscripción para limpiar al destruir el componente
  private cartSubscription: Subscription = new Subscription();

  // Signal computado para verificar si hay operaciones en curso
  isAnyOperationInProgress = computed(() =>
    this.isClearing() ||
    this.removingProductId() !== null ||
    this.updatingProductId() !== null ||
    this.isUpdatingSubtotal()
  );

  // Método para obtener o crear un signal para un producto
  getProductQuantity(productId: string, initialValue: string): Signal<number> {
    if (!this.productQuantities.has(productId)) {
      this.productQuantities.set(productId, signal<number>(Number(initialValue)));
    }
    return this.productQuantities.get(productId);
  }

  // Método para actualizar el signal de cantidad de un producto
  updateProductQuantitySignal(productId: string, newValue: number): void {
    if (this.productQuantities.has(productId)) {
      this.productQuantities.get(productId).set(newValue);
    } else {
      this.productQuantities.set(productId, signal<number>(newValue));
    }
  }

  // Método para verificar si un producto específico está siendo actualizado
  isProductUpdating(productId: string): boolean {
    return this.updatingProductId() === productId;
  }

  // Método para verificar si un producto específico está siendo eliminado
  isProductRemoving(productId: string): boolean {
    return this.removingProductId() === productId;
  }

  // Método para obtener o crear un signal para el estado expandido de un producto
  getProductExpanded(productId: string): Signal<boolean> {
    if (!this.expandedProducts.has(productId)) {
      this.expandedProducts.set(productId, signal<boolean>(false));
    }
    return this.expandedProducts.get(productId);
  }

  // Método para alternar el estado expandido de un producto
  toggleProductExpanded(productId: string): void {
    if (this.expandedProducts.has(productId)) {
      const currentSignal = this.expandedProducts.get(productId);
      currentSignal.set(!currentSignal());
    } else {
      this.expandedProducts.set(productId, signal<boolean>(true));
    }
  }

  // Método para verificar si un producto tiene variaciones o complementos
  hasVariationsOrComplements(product: any): boolean {
    return (product.variations && product.variations.length > 0) ||
      (product.complements && product.complements.length > 0);
  }

  // Método para verificar si un producto está deshabilitado
  isProductDisabled(productId: string): boolean {
    return this.cartProductsDisabled().some(disabled => disabled.product_id === productId);
  }




  constructor(public shopCartService: ShopCartService,
    private syncPricesCartService: SyncPricesCartService,
    private router: Router,
    private loaderService: LoaderSubitoService,
    private selectedAddressService: SelectedAddressService,
    private modalEmptyAddressesService: ModalEmptyAddressesService,
    private cartStorageService: CartStorageService,
    private store: Store<{ cart: CartModel }>,
    private prePurchaseService: PrePurchaseService,
    private notificationsService: NotificationsService,
  ) {
    this.nameEstablishment = toSignal(this.store.select(state => state.cart.CartData?.establishment_name));
    this.cartTotal = toSignal(this.store.select(state => state.cart.CartData?.total));

    // Suscribirse a los cambios del store desde el constructor
    this.cartSubscription.add(
      this.store.select(state => state.cart.CartData?.products).subscribe(products => {
        this.cartProducts.set(products || []);
        if (!products || products.length === 0) {
          this.productQuantities.clear();
          this.expandedProducts.clear();
        } else {
          // Actualizar signals de cantidades para cada producto
          products.forEach(product => {
            this.updateProductQuantitySignal(product.product_id, Number(product.quantity));
          });
        }
      })
    );
  }
  ngOnInit(): void {
    this.loadCartData();
  }

  ngOnDestroy(): void {
    this.cartSubscription.unsubscribe();
    this.productQuantities.clear();
    this.expandedProducts.clear();
  }

  loadCartData(): void {
    this.isUpdatingSubtotal.set(true);
    this.shopCartService.getProductsApi().subscribe({
      next: (response) => {
        // El store ya se actualiza automáticamente en el CartService
        // y la suscripción en el constructor maneja la actualización de la vista
        this.isUpdatingSubtotal.set(false);

        // Verificar productos deshabilitados después de cargar el carrito (con delay para evitar conflictos)
        setTimeout(() => {
          this.checkForDisabledProducts();
        }, 500);
      },
      error: () => {
        this.isUpdatingSubtotal.set(false);
      }
    });
  }

  // Método para verificar productos deshabilitados
  private checkForDisabledProducts(): void {
    // Solo verificar si hay productos en el carrito y no estamos ya verificando
    if (this.cartProducts().length > 0 && !this.isUpdatingSubtotal()) {
      // Verificar de forma silenciosa sin mostrar loader
      this.prePurchaseService.getCoordinates().subscribe({
        next: (response: PrePurchaseResponseCart) => {
          if (response.data.products_disable && response.data.products_disable.length > 0) {
            // Solo mostrar notificación si no había productos deshabilitados antes
            const hadDisabledProducts = this.cartProductsDisabled().length > 0;

            // Convertir productos deshabilitados al formato del carrito

            const disabledProducts: Product[] = response.data.products_disable.map((product: ProductsAble) => {
              // Buscar el shoppingCartDetails_id correcto en los productos del carrito actual
              return {
                product_id: product.pd_id.toString(),
                name: product.pd_name,
                description: '',
                priceProduct: product.pd_unit_price.toString(),
                image: product.pd_image,
                quantity: product.quantity.toString(),
                subtotal: product.total_product,
                shoppingCartDetails_id: product.shoppingCartDetails_id
              };
            });

            this.cartProductsDisabled.set(disabledProducts);
            this.hasDisabledProducts.set(true);

            // Solo mostrar notificación si es la primera vez que se detectan productos deshabilitados
            if (!hadDisabledProducts) {
              this.notificationsService.addNotification({
                type: 'warning',
                message: 'Algunos productos ya no están disponibles en tu carrito.',
                duration: 4000
              });
            }
          } else {
            // Limpiar productos deshabilitados si no hay ninguno
            this.cartProductsDisabled.set([]);
            this.hasDisabledProducts.set(false);
          }
        },
        error: (error) => {
          // Error silencioso para evitar spam en consola
        }
      });
    }
  }

  closeCart(): void {
    this.shopCartService.toggleCart(false);
  }
  clearCart() {
    this.isClearing.set(true);
    this.shopCartService.clearCart().subscribe({
      next: () => {
        this.cartProducts.set([]);
        // Limpiar todos los signals de cantidades y estados expandidos
        this.productQuantities.clear();
        this.expandedProducts.clear();
        this.isClearing.set(false);
      },
      error: () => {
        this.isClearing.set(false);
      }
    });
  }

  removeProduct(product: string) {
    this.removingProductId.set(product);
    this.shopCartService.removeProductFromCart(product).subscribe({
      next: () => {
        // Actualizar la lista de productos deshabilitados si el producto eliminado estaba en ella
        const updatedDisabled = this.cartProductsDisabled().filter(p => p.shoppingCartDetails_id !== product);
        this.cartProductsDisabled.set(updatedDisabled);
        this.hasDisabledProducts.set(updatedDisabled.length > 0);

        this.loadCartData();
        this.removingProductId.set(null);
      },
      error: () => {
        this.removingProductId.set(null);
      }
    });
  }

  updateQuantity(shoppingCartDetails_id: string) {
    this.updatingProductId.set(shoppingCartDetails_id);

    // Encontrar el producto para actualizar su cantidad localmente de forma optimista
    const currentProducts = this.cartProducts();
    const productIndex = currentProducts.findIndex(p => p.shoppingCartDetails_id === shoppingCartDetails_id);

    if (productIndex !== -1) {
      const product = currentProducts[productIndex];
      // Actualizar cantidad optimísticamente
      this.updateProductQuantitySignal(product.product_id, Number(product.quantity) + 1);
    }

    this.shopCartService.updateQuantity(shoppingCartDetails_id, 1).subscribe({
      next: (response) => {
        this.loadCartData(); // Recargar datos del carrito
        this.updatingProductId.set(null);
      },
      error: (error) => {
        // Revertir cambio optimista en caso de error
        if (productIndex !== -1) {
          const product = currentProducts[productIndex];
          this.updateProductQuantitySignal(product.product_id, Number(product.quantity));
        }
        this.updatingProductId.set(null);
      }
    });
  }
  decrementQuantity(shoppingCartDetails_id: string) {
    this.updatingProductId.set(shoppingCartDetails_id);

    // Encontrar el producto para actualizar su cantidad localmente de forma optimista
    const currentProducts = this.cartProducts();
    const productIndex = currentProducts.findIndex(p => p.shoppingCartDetails_id === shoppingCartDetails_id);

    if (productIndex !== -1) {
      const product = currentProducts[productIndex];
      const newQuantity = Math.max(1, Number(product.quantity) - 1); // No permitir cantidad menor a 1
      // Actualizar cantidad optimísticamente
      this.updateProductQuantitySignal(product.product_id, newQuantity);
    }

    this.shopCartService.updateQuantity(shoppingCartDetails_id, -1).subscribe({
      next: (response) => {
        this.loadCartData(); // Recargar datos del carrito
        this.updatingProductId.set(null);
      },
      error: (error) => {
        // Revertir cambio optimista en caso de error
        if (productIndex !== -1) {
          const product = currentProducts[productIndex];
          this.updateProductQuantitySignal(product.product_id, Number(product.quantity));
        }
        this.updatingProductId.set(null);
      }
    });
  }
  onImageError(event: any): void {
    event.target.src = this.defaultImage;
  }

  async proceedToPayment(): Promise<void> {
    // Verificar si hay una dirección seleccionada
    const selectedAddress = this.selectedAddressService.getCurrentSelectedAddress();

    if (!selectedAddress) {
      // Si no hay dirección seleccionada, mostrar el modal de direcciones vacías
      this.closeCart(); // Cerrar el carrito primero

      // Esperar a que el carrito se cierre antes de mostrar el modal
      await new Promise(resolve => setTimeout(resolve, 300));

      // Mostrar el modal de direcciones vacías
      this.modalEmptyAddressesService.toggleModal(true);
      return; // Detener el proceso de pago
    }

    // Si hay dirección seleccionada, continuar con el proceso de pago
    this.loaderService.showLoader();

    // this.shopCartService.syncPricesCart();
    this.closeCart(); // Cerrar el carrito primero

    // Esperar a que el carrito se cierre antes de continuar
    await new Promise(resolve => setTimeout(resolve, 300));

    this.prePurchaseService.getCoordinates().subscribe({
      next: (response: PrePurchaseResponseCart) => {
        // Si llegamos aquí, significa que no hay productos deshabilitados
        // o ya fueron manejados previamente
        if (this.hasDisabledProducts()) {
          this.notificationsService.addNotification({
            type: 'error',
            message: 'No puedes proceder al pago hasta eliminar los productos no disponibles.',
            duration: 3000
          });
          this.loaderService.hideLoader();
          return;
        }

        if (!response.message.includes('No todos los productos estan disponibles')) {
          this.loaderService.hideLoader(); // Add this line to hide the loader on success
          this.router.navigate(['/auth/detail-orden-payment']);
        } else {
          this.notificationsService.addNotification({
            type: 'warning',
            message: 'No todos los productos estan disponibles',
            duration: 3000

          });
          this.loaderService.hideLoader();

        }
      },
      error: (error) => {
        console.error('Error refreshing cart data:', error);
        // Ocultar el loader en caso de error
        this.loaderService.hideLoader();

        // Mostrar el modal de direcciones vacías cuando ocurre un error
        this.modalEmptyAddressesService.toggleModal(true);
      }
    });

  }
}
